import { type SaveCommentaryTemplateDto, CommentaryTemplateControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { useApiGen } from "$root/api/hooks";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import StoryfolioInner from "$root/functional-areas/storyfolio";
import { stableDefaultConfiguration } from "$root/functional-areas/storyfolio/common";
import { storyfolioRepository } from "$root/functional-areas/storyfolio/repository";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useParams } from "react-router";

function StoryfolioDetails(): React.JSX.Element {
	const { uuid } = useParams<{ uuid: string }>();
	const { push } = useTypedNavigation();
	const commentaryTemplateApi = useApiGen(CommentaryTemplateControllerApiFactory);

	async function createStoryfolioTemplate(template: SaveCommentaryTemplateDto) {
		try {
			const mode = template.default ? "create" : "update";
			if (mode === "create") {
				await commentaryTemplateApi.createTemplate({ ...template, default: false });
			}

			if (mode === "update") {
				await commentaryTemplateApi.updateTemplate(uuid, {
					...template,
					uuid,
					default: false,
				});
			}

			platformToast({
				children:
					mode === "create" ? "Commentary template created successfully" : "Commentary template updated successfully",
				icon: "Ask-ai",
				severity: "success",
			});
			push("Storyfolio/Studio", {});
		} catch (error) {
			platformToast({
				children: "Something went wrong please, try later",
				icon: "Ask-ai",
				severity: "error",
			});
			throw error;
		}
	}

	return (
		<ReactQueryWrapper
			queryKey={["loadStoryfolioData", uuid]}
			queryFn={() => axiosExtract(commentaryTemplateApi.getTemplate(uuid))}
		>
			{(template) => (
				<>
					<StoryfolioContextSync uuid={uuid} name={template.name} />
					<StoryfolioInner
						template={{
							...template,
							instrumentInsightsBlock: stableDefaultConfiguration.instrumentInsightsBlock(
								template.instrumentInsightsBlock,
							),
						}}
						onSubmit={async (editedTemplate) => {
							if (template.default) {
								await spawnCreateDialog({
									entityType: "template",
									onSubmitAsync: (name) => createStoryfolioTemplate({ ...editedTemplate, name }),
									checkIfNameIsAvailable: storyfolioRepository.isCommentaryNameIsAvailable,
									placeholder: editedTemplate.name ?? "Untitled template",
									header: "Rename Template",
								}).catch(noop);
								return;
							}

							await createStoryfolioTemplate(editedTemplate);
						}}
					/>
				</>
			)}
		</ReactQueryWrapper>
	);
}

/**
 * Expose the open storyfolio (commentary template) to SphereAI so it can ground answers and
 * navigate here. Kept as a tiny child of the query wrapper so it can read the loaded name.
 */
function StoryfolioContextSync({ uuid, name }: { uuid?: string; name?: string }): null {
	useFunctionalAreas(() => ({ areas: "storyfolio", data: { uuid, name } }), [uuid, name]);
	return null;
}

export default StoryfolioDetails;
